import { Titled } from 'react-titled';
import { styled } from '@mui/material';
import type { FC, ReactNode } from 'react';
import { Suspense } from 'react';
import Box from '@mui/material/Box';
import { keyframes } from '@emotion/react';
import SideMenu from './SideMenu';
import CircularProgress from '../common/CircularProgress';
import { useLocation } from 'react-router-dom';
import CabinetMenu from './CabinetMenu';

interface LayoutProps {
  children: ReactNode;
  title: string;
}

const animationName = keyframes`
    0% {
        background-position: 0 81%;
    }
    50% {
        background-position: 100% 20%;
    }
    100% {
        background-position: 0 81%;
    }
`;

const BackgroundContainer = styled(Box)(() => ({
  position: 'relative',
  display: 'flex',
  width: '100%',
  minWidth: '990px',
  overflowX: 'auto',
  height: '100vh',
  minHeight: '100vh',
  zIndex: 1,
  background: 'linear-gradient(242deg, #e8ebff, #f6f8ff, #f3f5ff)',
  backgroundSize: '500% 500%',
  animation: `${animationName} 26s ease infinite`,
  flexDirection: 'row',
  gap: '2px',
}));

const MainContainer = styled(Box)(({ theme: t }) => ({
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  height: '100vh',
  overflow: 'hidden',
  overflowY: 'auto',
  gap: 0,
  padding: t.spacing(2),
  paddingTop: t.spacing(1),
}));

export const LayoutContainer: FC<LayoutProps> = ({ title, children }) => (
  <Titled title={title}>
    <BackgroundContainer>{children}</BackgroundContainer>
  </Titled>
);
const Layout: FC<LayoutProps> = ({ title, children }) => (
  <Suspense fallback={<CircularProgress />}>
    <LayoutContainer title={title}>
      <SideMenu />
      <MainContainer>{children}</MainContainer>
    </LayoutContainer>
  </Suspense>
);

export const LayoutCabinet: FC<LayoutProps> = ({ title, children }) => {
  const location = useLocation();
  const isBgWhite = !location.pathname.includes('overview');

  return (
    <Suspense fallback={<CircularProgress />}>
      <LayoutContainer title={title}>
        <SideMenu />
        <CabinetMenu />
        <MainContainer
          sx={{ backgroundColor: isBgWhite ? 'white' : 'transparent' }}
        >
          {children}
        </MainContainer>
      </LayoutContainer>
    </Suspense>
  );
};

export default Layout;
